/**
 * Computus: Easter date calculation
 *
 * Uses the Anonymous Gregorian algorithm (the "Meeus/Jones/Butcher" algorithm)
 * to compute Easter Sunday for any Gregorian calendar year.
 *
 * This is the standard algorithm used by the Roman Catholic Church since
 * the Gregorian calendar reform of 1582.
 */

/**
 * Calculate the date of Easter Sunday for a given year.
 * Valid for any year in the Gregorian calendar (1583+).
 */
export function computeEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

/** Add or subtract days from a date */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Check if two dates are the same calendar day */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Get the day of the week (0 = Sunday, 6 = Saturday) */
export function getDayOfWeek(date: Date): number {
  return date.getDay();
}

/** Check if the date falls on a Sunday */
export function isSunday(date: Date): boolean {
  return date.getDay() === 0;
}

/** Get the next occurrence of a specific weekday on or after a date */
export function nextWeekday(date: Date, weekday: number): Date {
  const result = new Date(date);
  while (result.getDay() !== weekday) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}

/** Get the previous occurrence of a specific weekday on or before a date */
export function prevWeekday(date: Date, weekday: number): Date {
  const result = new Date(date);
  while (result.getDay() !== weekday) {
    result.setDate(result.getDate() - 1);
  }
  return result;
}

/** Format a date as YYYY-MM-DD */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Format as MMDDYY for USCCB URL pattern */
export function formatUSCCBDate(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = String(date.getFullYear()).slice(2);
  return `${m}${d}${y}`;
}

/** Check if a date falls between (inclusive) two dates */
export function isDateInRange(date: Date, start: Date, end: Date): boolean {
  const d = date.getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59).getTime();
  return d >= s && d <= e;
}

/** Get number of days between two dates */
export function daysBetween(a: Date, b: Date): number {
  const msPerDay = 86400000;
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((utc2 - utc1) / msPerDay);
}
